import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Activity, RefreshCw, Loader2, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScoreRing = ({ score, size = 96 }) => {
  const radius = (size / 2) - 6;
  const stroke = 6;
  const circumference = 2 * Math.PI * radius;
  const progress = score != null ? (score / 100) * circumference : 0;
  const color = score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-black" style={{ fontFamily: 'Manrope, sans-serif', color }}>
          {score ?? '-'}
        </span>
      </div>
    </div>
  );
};

const TAG_COLORS = {
  vague: 'bg-yellow-100 text-yellow-800',
  redundant: 'bg-orange-100 text-orange-800',
  double_question: 'bg-red-100 text-red-800',
  too_broad: 'bg-purple-100 text-purple-800',
  too_specific: 'bg-indigo-100 text-indigo-800',
  high_cognitive_load: 'bg-rose-100 text-rose-800',
  low_information_value: 'bg-gray-100 text-gray-700',
};

const POSITION_COLORS = {
  early: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  mid: 'bg-blue-50 text-blue-700 border-blue-200',
  late: 'bg-amber-50 text-amber-700 border-amber-200',
  'follow-up only': 'bg-purple-50 text-purple-700 border-purple-200',
};

export const InterviewHealthPanel = ({ qrId }) => {
  const { token } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchCached();
  }, [qrId]);

  const fetchCached = async () => {
    try {
      const res = await axios.get(`${API}/intelligence/question-analysis/${qrId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.status !== 'not_analyzed') {
        setAnalysis(res.data);
      }
    } catch {
      // No cached analysis
    } finally {
      setInitialLoad(false);
    }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/intelligence/analyze-questions/${qrId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000,
      });
      setAnalysis(res.data);
      toast.success('Question analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) return null;

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="interview-health-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#1d4ed8]" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Interview Health
          </h2>
          <span className="text-xs bg-[#EFF6FF] text-[#1d4ed8] px-2 py-0.5 rounded-full font-medium">
            AI-Powered
          </span>
        </div>
        <Button
          size="sm"
          onClick={runAnalysis}
          disabled={loading}
          className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
          data-testid="run-question-analysis-btn"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Analyzing...</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-1.5" /> {analysis ? 'Re-analyze' : 'Analyze Questions'}</>
          )}
        </Button>
      </div>

      {!analysis && !loading && (
        <p className="text-sm text-[#475569] text-center py-6">
          Analyze your interview questions for quality, cognitive load, and information yield.
        </p>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#1d4ed8]" />
          <p className="text-sm text-[#475569]">AI is evaluating your interview questions...</p>
        </div>
      )}

      {analysis && !loading && (
        <div className="space-y-5">
          {/* Overall Health Score */}
          <div className="flex items-center gap-5">
            <ScoreRing score={analysis.interview_health_score} />
            <div>
              <p className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Interview Health Score
              </p>
              <p className="text-xs text-[#475569] mt-0.5">
                Based on {analysis.questions?.length || 0} questions &middot; Last analyzed {new Date(analysis.analyzed_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Per-question breakdown */}
          <div className="space-y-3" data-testid="question-analysis-list">
            {analysis.questions?.map((q, i) => (
              <div
                key={q.question_id || i}
                className="p-4 rounded-lg border border-[#E2E8F0] bg-[#FAFBFC] hover:border-[#CBD5E1] transition-colors"
                data-testid={`question-analysis-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      {q.question_quality_score >= 70 ? (
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                      ) : q.question_quality_score >= 40 ? (
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                      <span className="text-sm font-medium truncate">{q.question_text}</span>
                    </div>

                    {/* Scores */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569] mb-2">
                      <span>Quality: <strong className={q.question_quality_score >= 70 ? 'text-green-700' : q.question_quality_score >= 40 ? 'text-amber-700' : 'text-red-700'}>{q.question_quality_score}</strong></span>
                      <span>Cognitive Load: <strong>{q.cognitive_load_score}</strong></span>
                      <span>Info Yield: <strong>{q.information_yield_estimate}</strong></span>
                    </div>

                    {/* Tags */}
                    {q.issue_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {q.issue_tags.map(tag => (
                          <span key={tag} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-700'}`}>
                            {tag.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Suggested rewrite */}
                    {q.suggested_rewrite && (
                      <div className="flex items-start gap-1.5 text-xs mt-1">
                        <ArrowRight className="h-3.5 w-3.5 text-[#1d4ed8] shrink-0 mt-0.5" />
                        <span className="text-[#334155]"><span className="font-medium text-[#1d4ed8]">Suggested:</span> {q.suggested_rewrite}</span>
                      </div>
                    )}
                  </div>

                  {/* Position badge */}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${POSITION_COLORS[q.recommended_position] || POSITION_COLORS.mid}`}>
                    {q.recommended_position}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
